import React from 'react';
import { Card, Checkbox, Typography } from 'antd';
import { typography } from '@doer/frontend-components/theme';
import { CreativityContentData, CreativityType } from '../../types/banner';
import { useModal } from '../../context/modal';
import { getBannerStatusData, getDisplayContent, getFirstImage, getFirstOptimizedImage } from '../../utils';
import { useResponsiveSizes } from '../../hooks/use-responsive-sizes';
import { useSendToSocial } from '../../hooks/banners/use-send-to-social';

import { getHandlerForTemplate } from '../../handlers';
import { BannerMetadataLine } from '../../types/library-metadata';
import { StatusTag } from '../ui/status-tag';

const { Text } = Typography;

const getBannerMetadata = (creativity: CreativityContentData): BannerMetadataLine[] => {
  // Usar bannerType del creatividad en lugar de clientId del usuario activo
  // Si el creatividad no tiene template (legacy/migrado), clientHandler será vacío y usará fallback
  const clientHandler = getHandlerForTemplate(creativity.bannerType as CreativityType);

  if (clientHandler.getBannerMetadata) {
    const customLines = clientHandler.getBannerMetadata(creativity);
    if (customLines && customLines.length > 0) {
      return customLines;
    }
  }

  // Fallback para banners sin template o sin metadata personalizada.
  // reel/video no tienen `title` → usan `caption`.
  const caption = (creativity as { caption?: string }).caption;
  return [{ text: creativity.title || caption || 'Asset', fontWeight: 'bold' }];
};

export const BannerCard: React.FC<{ creativity: CreativityContentData }> = ({ creativity }) => {
  const { openAsset } = useModal();
  const { fontSize, spacing, screens } = useResponsiveSizes();
  const { setVisibleInSocial } = useSendToSocial();

  // Estado optimista del checkbox "Enviar a Social"
  const [visibleInSocial, setVisibleInSocialState] = React.useState(creativity.visibleInSocial === true);
  React.useEffect(() => {
    setVisibleInSocialState(creativity.visibleInSocial === true);
  }, [creativity.visibleInSocial]);

  const handleSocialChange = async (checked: boolean) => {
    setVisibleInSocialState(checked); // update optimista
    const res = await setVisibleInSocial(creativity.bannerId, checked);
    if (!res) {
      setVisibleInSocialState(prev => !prev); // revertir si falla
    }
  };

  // reel/video no tienen mediaFiles de imagen → usan su thumbnail (`posterUrl`).
  const poster = (creativity as { posterUrl?: string }).posterUrl;
  const image = getFirstOptimizedImage(creativity) || getFirstImage(creativity) || poster;

  // Fallback de imagen: si el poster 404ea (típico en reels PROCESSING cuyo displayUrl
  // de IG caduca), el <img> queda roto y la card se achica. onError → placeholder.
  const [imgError, setImgError] = React.useState(false);

  const [imgReady, setImgReady] = React.useState(!image);
  React.useEffect(() => {
    setImgError(false);
    if (!image) {
      setImgReady(true);
      return;
    }
    setImgReady(false);
    const preload = new window.Image();
    preload.onload = () => setImgReady(true);
    preload.onerror = () => {
      setImgError(true);
      setImgReady(true);
    };
    preload.src = image;
  }, [image]);

  const contentType = (creativity as { type?: string }).type;
  const isVideoLike = contentType === 'reel' || contentType === 'video';
  // "Enviar a Social" sólo en reels: un reel YA es la entidad catalogable (no se mintea un
  // type=video derivado, como sí pasa con las creativities). Un avatar también es un video,
  // pero NO va al catálogo de social → por eso gateamos por reel, no por "tiene video".
  const isReel = contentType === 'reel';
  const showImage = !!image && !imgError;

  if (!imgReady) return null;

  const statusData = getBannerStatusData(creativity);
  const metadataLines = getBannerMetadata(creativity);

  const cardStyle = {
    width: '100%',
    borderRadius: '16px',
    border: '1px solid #F0EDED',
    boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.05)',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    position: 'relative' as const,
    overflow: 'hidden' as const
  };

  const imageStyle = {
    width: '100%',
    height: 'auto',
    display: 'block',
    backgroundColor: image ? 'transparent' : '#f5f5f5'
  };

  const metadataStyle = {
    background: '#ffffff',
    padding: `${spacing.sm}px ${spacing.md}px`
  };

  const placeholderStyle = {
    width: '100%',
    height: screens.xs ? 100 : screens.sm ? 150 : 200,
    backgroundColor: '#f5f5f5',
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    color: '#bfbfbf',
    fontSize: fontSize.sm,
    fontWeight: 500,
    gap: `${spacing.xs}px`,
    boxSizing: 'border-box' as const
  };

  return (
    <div style={{ width: '100%' }}>
      <Card
        style={cardStyle}
        styles={{
          body: {
            padding: 0,
            height: '100%',
            width: '100%',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
        onClick={() => openAsset(creativity.bannerId)}
        hoverable
      >
        {/* Status badge — solo si hay un label real. Sin label (p.ej. reels en PROCESSING/READY,
            que no están en el mapa de BannerStatus) NO mostramos badge, en vez del viejo "Enviado"
            por defecto que no significaba nada. */}
        {statusData?.label && creativity.status !== 'DONE' ? (
          <div
            style={{
              position: 'absolute',
              top: spacing.xs,
              left: spacing.xs,
              zIndex: 4
            }}
          >
            <StatusTag label={statusData.label} variant='overlay' size='small' />
          </div>
        ) : null}

        {/* Checkbox "Enviar a Social" (solo en reels) */}
        {isReel && (
          <div
            style={{
              position: 'absolute',
              top: spacing.xs,
              right: spacing.xs,
              zIndex: 4,
              background: 'rgba(0,0,0,0.55)',
              borderRadius: '6px',
              padding: '2px 8px'
            }}
            onClick={e => e.stopPropagation()}
          >
            <Checkbox
              checked={visibleInSocial}
              onChange={e => handleSocialChange(e.target.checked)}
              style={{ color: '#ffffff', fontSize: fontSize.sm }}
            >
              <span style={{ color: '#ffffff', fontSize: fontSize.sm }}>Enviar a Social</span>
            </Checkbox>
          </div>
        )}

        {/* Imagen o placeholder (type-aware: video/reel muestran ícono play + caption + tipo) */}
        {showImage ? (
          <img src={image} alt={getDisplayContent(creativity)} style={imageStyle} onError={() => setImgError(true)} />
        ) : (
          <div style={placeholderStyle}>
            {isVideoLike ? (
              <svg
                viewBox='0 0 24 24'
                width={screens.xs ? 28 : 40}
                height={screens.xs ? 28 : 40}
                fill='none'
                stroke='currentColor'
                strokeWidth='1.5'
                style={{ opacity: 0.6 }}
              >
                <circle cx='12' cy='12' r='9' />
                <polygon points='10 8.5 16.5 12 10 15.5' fill='currentColor' stroke='none' />
              </svg>
            ) : (
              <svg
                viewBox='0 0 24 24'
                width={screens.xs ? 28 : 40}
                height={screens.xs ? 28 : 40}
                fill='none'
                stroke='currentColor'
                strokeWidth='1.5'
                style={{ opacity: 0.5 }}
              >
                <rect x='3' y='3' width='18' height='18' rx='2' ry='2' />
                <circle cx='8.5' cy='8.5' r='1.5' />
                <polyline points='21 15 16 10 5 21' />
              </svg>
            )}
            <span
              style={{
                maxWidth: '90%',
                textAlign: 'center',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical' as const,
                color: isVideoLike ? '#8c8c8c' : '#bfbfbf'
              }}
            >
              {isVideoLike ? getDisplayContent(creativity) : 'Sin imagen'}
            </span>
            {isVideoLike && (
              <span
                style={{
                  fontSize: fontSize.xs,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase' as const,
                  background: '#e5e7eb',
                  borderRadius: 4,
                  padding: '1px 6px',
                  color: '#6b7280'
                }}
              >
                {contentType === 'reel' ? 'Reel' : 'Video'}
              </span>
            )}
          </div>
        )}

        <div style={metadataStyle}>
          {metadataLines.map((line, index) => {
            const isFirst = index === 0;

            return (
              <Text
                key={index}
                strong={line.fontWeight === 'bold' || (isFirst && !line.fontWeight)}
                style={{
                  fontFamily: isFirst ? typography.fontFamily.ui : undefined,
                  fontWeight: isFirst ? typography.fontWeight.semibold : undefined,
                  fontSize: isFirst ? typography.fontSize.sm : line.fontSize ? fontSize[line.fontSize] : fontSize.base,
                  lineHeight: isFirst ? '15px' : undefined,
                  letterSpacing: isFirst ? '0.5px' : undefined,
                  textTransform: isFirst ? 'uppercase' : undefined,
                  display: 'block',
                  color: line.color || (isFirst ? '#22252F' : '#565C76'),
                  marginBottom: index < metadataLines.length - 1 ? spacing.xs : 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {line.text}
              </Text>
            );
          })}
        </div>
      </Card>
    </div>
  );
};
